// Images a quiz page paints inline (IZ exam formulas and figures), loaded once per URL.
// CORS-loaded so the painted page stays readable by toDataURL; the IZ CDN answers `*`.

interface Entry {
  state: 'loading' | 'ready' | 'error';
  image: HTMLImageElement;
  settled: Promise<void>;
}

const entries: Record<string, Entry> = {};
const listeners: (() => void)[] = [];

function notify(): void {
  listeners.slice().forEach((listener) => listener());
}

function entryFor(url: string): Entry {
  const existing = entries[url];
  if (existing) return existing;
  const image = new Image();
  image.crossOrigin = 'anonymous';
  let settle: () => void = () => undefined;
  const entry: Entry = { state: 'loading', image, settled: new Promise<void>((resolve) => { settle = resolve; }) };
  image.onload = () => { entry.state = image.naturalWidth > 0 && image.naturalHeight > 0 ? 'ready' : 'error'; settle(); notify(); };
  image.onerror = () => { entry.state = 'error'; settle(); notify(); };
  entries[url] = entry;
  image.src = url;
  return entry;
}

/** The loaded image for `url`, or null while it loads or after it failed. */
export function quizImage(url: string): HTMLImageElement | null {
  const entry = entries[url];
  return entry && entry.state === 'ready' ? entry.image : null;
}

/** Starts every load and resolves once all settled, or at `timeoutMs`, whichever is first. */
export function loadQuizImages(urls: string[], timeoutMs: number): Promise<void> {
  if (urls.length === 0) return Promise.resolve();
  const all = Promise.all(urls.map((url) => entryFor(url).settled)).then(() => undefined);
  return Promise.race([all, new Promise<void>((resolve) => { window.setTimeout(resolve, timeoutMs); })]);
}

/** Called whenever an image settles, so a page painted before it can repaint. */
export function subscribeQuizImages(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index >= 0) listeners.splice(index, 1);
  };
}
